import L from "leaflet";
import { useEffect } from "react";
import { DRAW_PATH_OPTIONS, EDITABLE_EVENTS } from "../constants/fence";
import type {
	CircleData,
	EditableLayer,
	Fence,
	LayerWithEditHooks,
	UseFenceEditableLayerParams,
} from "../types/fence";
import {
	closePolygon,
	parseCircleFromWKT,
	parsePolygonFromWKT,
} from "../utils/fence.utils";

function ensureMapEditTools(map: import("leaflet").Map): void {
	const mapWithEditable = map as import("leaflet").Map & {
		editTools?: unknown;
	};
	if (
		!mapWithEditable.editTools &&
		(L as typeof L & { Editable?: unknown }).Editable
	) {
		const LeafletWithEditable = L as typeof L & {
			Editable: new (mapInstance: import("leaflet").Map) => unknown;
		};
		mapWithEditable.editTools = new LeafletWithEditable.Editable(map);
	}
}

function addEditableLayerForFence(
	map: import("leaflet").Map,
	fence: Fence,
	editableLayerRef: UseFenceEditableLayerParams["editableLayerRef"]
): boolean {
	if (fence.type === 1) {
		const circleData = parseCircleFromWKT(fence.area);
		if (!circleData) {
			return false;
		}
		const circleLayer = L.circle(circleData.center, {
			radius: circleData.radius,
			...DRAW_PATH_OPTIONS,
		}).addTo(map);
		editableLayerRef.current = circleLayer;
		return true;
	}

	const polygonData = parsePolygonFromWKT(fence.area);
	if (!polygonData) {
		return false;
	}
	const lastPoint = polygonData.points.at(-1);
	const polygonPoints =
		polygonData.points.length > 1 &&
		lastPoint &&
		polygonData.points[0][0] === lastPoint[0] &&
		polygonData.points[0][1] === lastPoint[1]
			? polygonData.points.slice(0, -1)
			: polygonData.points;
	const polygonLayer = L.polygon(polygonPoints, {
		...DRAW_PATH_OPTIONS,
	}).addTo(map);
	editableLayerRef.current = polygonLayer;
	return true;
}

function readEditedGeometry(
	editedLayer: EditableLayer,
	setTempCircle: (circle: CircleData | null) => void,
	setTempPolygon: (points: [number, number][]) => void
): void {
	if (editedLayer instanceof L.Circle) {
		const center = editedLayer.getLatLng();
		setTempCircle({
			center: [center.lat, center.lng],
			radius: editedLayer.getRadius(),
		});
		return;
	}

	const polygonLatLngs = editedLayer.getLatLngs();
	const firstRing = Array.isArray(polygonLatLngs[0])
		? (polygonLatLngs[0] as L.LatLng[])
		: (polygonLatLngs as L.LatLng[]);

	const points = firstRing.map(
		(point) => [point.lat, point.lng] as [number, number]
	);
	if (points.length >= 3) {
		setTempPolygon(closePolygon(points));
		return;
	}
	setTempPolygon(points);
}

export function useFenceEditableLayer({
	mapRef,
	editableLayerRef,
	clearTempLayers,
	clearEditableLayer,
	drawingMode,
	isEditingFence,
	isRedrawingEdit,
	setTempCircle,
	setTempPolygon,
}: UseFenceEditableLayerParams) {
	useEffect(() => {
		const map = mapRef.current;
		if (!map) {
			return;
		}
		if (!isEditingFence || isRedrawingEdit || !drawingMode) {
			clearEditableLayer();
			return;
		}

		ensureMapEditTools(map);
		clearTempLayers();
		clearEditableLayer();

		const attached = addEditableLayerForFence(
			map,
			isEditingFence,
			editableLayerRef
		);
		if (!attached) {
			return;
		}

		const syncEditedShape = () => {
			const editedLayer = editableLayerRef.current;
			if (!editedLayer) {
				return;
			}
			readEditedGeometry(editedLayer, setTempCircle, setTempPolygon);
		};

		const layer = editableLayerRef.current as LayerWithEditHooks;
		layer.enableEdit?.(map);
		layer.editing?.enable?.();
		syncEditedShape();

		for (const eventName of EDITABLE_EVENTS) {
			layer.on(eventName, syncEditedShape);
		}
		map.on("mouseup", syncEditedShape);

		return () => {
			for (const eventName of EDITABLE_EVENTS) {
				layer.off(eventName, syncEditedShape);
			}
			map.off("mouseup", syncEditedShape);
			clearEditableLayer();
		};
	}, [
		mapRef,
		drawingMode,
		isEditingFence,
		isRedrawingEdit,
		setTempCircle,
		setTempPolygon,
		clearEditableLayer,
		clearTempLayers,
		editableLayerRef,
	]);
}
